import type { MusicAnalytics } from "@/api/__generated__/models";
import { cn } from "@/lib/utils";
import { useAnalyticsMonth } from "@/routes/custom-music/dsp-analytics/context/dsp-analytics-context";
import { formatCurrency, formatNumber, formatPercentage } from "@/utils/format";
import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  DropdownTriggerButton,
  Icon,
  InteractiveTable,
  InteractiveTableHeaderRow,
  InteractiveTableRow,
  ShadowBox,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Typography,
} from "@backpac/monica";
import React, { useEffect, useMemo, useState } from "react";

enum SortOption {
  HIGHEST_REVENUE = "HIGHEST_REVENUE",
  LOWEST_REVENUE = "LOWEST_REVENUE",
}

const SORT_OPTIONS = {
  [SortOption.HIGHEST_REVENUE]: { label: "Highest Revenue", icon: "arrow-up" },
  [SortOption.LOWEST_REVENUE]: { label: "Lowest Revenue", icon: "arrow-down" },
};

interface AdditionalColumn<T> {
  label: string;
  render: (item: T) => React.ReactNode;
  position: "start" | "end";
}

interface RevenueBreakdownTableProps<T> {
  title: string;
  analytics: T[];
  selectedIds?: string[];
  setSelectedIds?: (ids: string[]) => void;
  additionalColumns?: AdditionalColumn<T>[];
}

export const RevenueBreakdownTable = <T extends MusicAnalytics>(props: RevenueBreakdownTableProps<T>) => {
  const [analytics, setAnalytics] = useState<T[]>(props.analytics);
  const [sort, setSort] = useState<SortOption>(SortOption.HIGHEST_REVENUE);

  useEffect(() => {
    if (sort === SortOption.HIGHEST_REVENUE) {
      setAnalytics(props.analytics.toSorted((a, b) => b.revenue - a.revenue));
    } else {
      setAnalytics(props.analytics.toSorted((a, b) => a.revenue - b.revenue));
    }
  }, [sort, props.analytics]);

  return (
    <ShadowBox className="flex flex-col">
      <RevenueBreakdownTable.Header sort={sort} setSort={setSort} title={props.title} />
      <RevenueBreakdownTable.Table {...props} analytics={analytics} />
    </ShadowBox>
  );
};

RevenueBreakdownTable.Header = ({
  sort,
  setSort,
  title,
}: {
  sort: SortOption;
  setSort: (sort: SortOption) => void;
  title: string;
}) => {
  const { formatedMonthRange } = useAnalyticsMonth();
  return (
    <div className="flex flex-row justify-between items-center p-6 pb-2">
      <div className="flex flex-col gap-1.5">
        <Typography size="md" font="unbounded" color="textPrimary">
          {title}
        </Typography>
        <Typography size="xs" color="textMuted">
          {formatedMonthRange}
        </Typography>
      </div>
      <div className="flex items-center gap-2">
        <RevenueBreakdownTable.Sort value={sort} onChange={setSort} />
        <Button variant="outlined" size="sm" rightAddon={<Icon name="download" iconSize="12px" />}>
          Export
        </Button>
      </div>
    </div>
  );
};

interface SortProps {
  onChange: (value: SortOption) => void;
  value: SortOption;
}

RevenueBreakdownTable.Sort = ({ value, onChange }: SortProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Dropdown open={open} onOpenChange={setOpen}>
      <DropdownTrigger asChild>
        <DropdownTriggerButton size="md" rightAddon={<Icon name="swap" iconSize="12px" />} className="gap-2">
          <Typography size="xs" weight={600} color="textPrimary">
            {SORT_OPTIONS[value].label}
          </Typography>
        </DropdownTriggerButton>
      </DropdownTrigger>
      <DropdownContent>
        <div className="max-h-[360px] min-w-[200px] overflow-y-auto">
          {Object.entries(SORT_OPTIONS).map(([sortOption, { label }]) => (
            <DropdownItem
              key={label}
              className="flex items-center gap-1.5"
              onSelect={() => onChange(sortOption as SortOption)}
            >
              <div className="flex flex-1 items-center justify-between gap-4px">
                <Typography size="sm" weight={600} color="textPrimary">
                  {label}
                </Typography>
                {value === sortOption && <Icon name="check" className="size-16px" />}
              </div>
            </DropdownItem>
          ))}
        </div>
      </DropdownContent>
    </Dropdown>
  );
};

RevenueBreakdownTable.Table = <T extends MusicAnalytics>({
  analytics,
  selectedIds,
  setSelectedIds,
  additionalColumns,
}: RevenueBreakdownTableProps<T>) => {
  const analyticsWithShare = useMemo(() => {
    const totalRevenue = analytics.reduce((acc, curr) => acc + curr.revenue, 0);
    return analytics.map((item) => ({
      ...item,
      share: (item.revenue / totalRevenue) * 100,
    }));
  }, [analytics]);

  const renderMonthlyData = (item: MusicAnalytics & { share: number }) => {
    return (
      <>
        {item.monthlyData
          .filter((md) => md.salesData.length > 0)
          .map((monthData) => (
            <React.Fragment key={monthData.settlementMonth}>
              <TableRow key={monthData.settlementMonth} className="border-0">
                <TableCell className="w-[20px] max-w-[20px]" />
                {selectedIds != null && <TableCell className="w-[44px] max-w-[44px]" />}
                <TableCell
                  className="py-2"
                  colSpan={1 + (additionalColumns?.filter((c) => c.position === "start").length || 0)}
                >
                  <Typography size="xs" weight={600} color="textSecondary">
                    {monthData.settlementMonth}
                  </Typography>
                </TableCell>
                <TableCell className="py-2">
                  <Typography size="xs" weight={600} color="textSecondary">
                    {formatCurrency(monthData.revenue)}
                  </Typography>
                </TableCell>
                <TableCell className="py-2">
                  <Typography size="xs" weight={600} color="textSecondary">
                    {formatNumber(monthData.streams, 0)}
                  </Typography>
                </TableCell>
              </TableRow>
              {monthData.salesData.map((sale, idx) => (
                <TableRow
                  key={`${monthData.settlementMonth}-${sale.saleMonth}`}
                  className={cn("border-0", idx === monthData.salesData.length - 1 && "border-b")}
                >
                  <TableCell className="w-[20px] max-w-[20px]" />
                  {selectedIds != null && <TableCell className="w-[44px] max-w-[44px]" />}
                  <TableCell
                    className="py-2"
                    colSpan={1 + (additionalColumns?.filter((c) => c.position === "start").length || 0)}
                  >
                    <div className="flex items-center gap-2">
                      ⤷
                      <Typography size="xs" color="textMuted">
                        {sale.saleMonth} Sales
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    <Typography size="xs" color="textMuted">
                      {formatCurrency(sale.revenue)}
                    </Typography>
                  </TableCell>
                  <TableCell className="py-2">
                    <Typography size="xs" color="textMuted">
                      {formatNumber(sale.streams, 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
      </>
    );
  };

  return (
    <InteractiveTable
      onCheckChange={setSelectedIds}
      checkedIds={selectedIds}
      allIds={analytics.map((item) => item.name)}
      className="shadow-none"
    >
      <TableHeader>
        <InteractiveTableHeaderRow showExpandColumn showCheckboxColumn={selectedIds != null}>
          {additionalColumns
            ?.filter((column) => column.position === "start")
            .map((column) => (
              <TableHead key={column.label}>{column.label}</TableHead>
            ))}
          <TableHead>Share</TableHead>
          <TableHead>Revenue</TableHead>
          <TableHead>Stream</TableHead>
          {additionalColumns
            ?.filter((column) => column.position === "end")
            .map((column) => (
              <TableHead key={column.label}>{column.label}</TableHead>
            ))}
        </InteractiveTableHeaderRow>
      </TableHeader>
      <TableBody>
        {analyticsWithShare.map((item) => (
          <InteractiveTableRow
            key={item.name}
            checkboxId={selectedIds == null ? undefined : item.name}
            expandedTableRow={renderMonthlyData(item)}
          >
            {additionalColumns
              ?.filter((column) => column.position === "start")
              .map((column) => (
                <TableCell key={column.label}>{column.render(item)}</TableCell>
              ))}
            <TableCell>
              <Typography size="xs" color="textPrimary">
                {formatPercentage(item.share)}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography size="xs" color="textPrimary">
                {formatCurrency(item.revenue)}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography size="xs" color="textPrimary">
                {formatNumber(item.streams, 0)}
              </Typography>
            </TableCell>
            {additionalColumns
              ?.filter((column) => column.position === "end")
              .map((column) => (
                <TableCell key={column.label}>{column.render(item)}</TableCell>
              ))}
          </InteractiveTableRow>
        ))}
      </TableBody>
    </InteractiveTable>
  );
};
